import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';

import { ImportType } from '../../../../../../components/csv-import/data/types/import-type';
import { SellerSettings } from '../../../../../../enums/seller-settings/seller-settings';
import { useOnComponentMount } from '../../../../../../hooks/use-on-component-mount';
import { useSellerSetting } from '../../../../../../hooks/use-seller-setting';
import { Segment } from '../../../../../../services/analytics-services/segment';
import { clearImportStatus } from '../../../../../../store/csv-import/csv-import.actions';
import Global from '../../../../../../store/global-selectors';
import { saveNewSellerSettings } from '../../../../../../store/seller/seller.actions';
import { ClickEvents, EventSourceStrings } from '../../../../../../tracking/tracking-constants';
import { updateSellerSettingsBulk } from '../../../../../settings/api/save-settings';
import { bulkUpdateConnections } from '../../../../../settings/inventory-sources/api';
import { InventoryConnectionTerminusTypeEnum, InventoryConnectionValidStatusEnum } from '../../../../../settings/inventory-sources/enums';
import useConsolidatedInventorySources from '../../../../../settings/inventory-sources/use-consolidated-inventory-sources';
import { messages } from '../../messages';
import { useFTUWizardContext } from '../ftu-wizard-context';

const InventorySyncContext = createContext();

export const useInventorySyncContext = () => {
  const context = useContext(InventorySyncContext);
  if (!context) {
    throw new Error('InventorySyncContext must be used within InventorySyncProvider');
  }
  return context;
};

const filterValidConnections = (connections) => {
  const validStatuses = Object.values(InventoryConnectionValidStatusEnum);
  return connections.filter(({ status }) => validStatuses.includes(status.toLowerCase()));
};

/**
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @returns {JSX.Element}
 */
export const InventorySyncProvider = ({ children }) => {
  const { loading, connections: originalConnections, fetchConsolidatedInventoryConnections } = useConsolidatedInventorySources();

  const [selectedConnections, setSelectedConnections] = useState([]);

  const [isUpdatingConfig, setIsUpdatingConfig] = useState(false);

  const updateConfig = async () => {
    try {
      setIsUpdatingConfig(true);
      const connectionsToUpdate = selectedConnections.filter((connection) => connection.terminusType !== undefined);
      if (connectionsToUpdate.length > 0) {
        await bulkUpdateConnections(connectionsToUpdate);
      }
    } catch {
      NotificationService.showNotification({
        notificationType: NOTIFICATION_TYPE_ERROR,
        title: <FormattedMessage {...messages.inventorySyncUpdateConfigErrorTitle} />,
        content: <FormattedMessage {...messages.inventorySyncUpdateConfigErrorContent} />,
      });
    } finally {
      setIsUpdatingConfig(false);
    }
  };

  const fetchAndSetActiveConfig = useCallback(async () => {
    // don't refetch connections when config is being updated
    // it is possible that new refech will overwrite the changes made by the user
    if (isUpdatingConfig) {
      return;
    }
    const allConnections = await fetchConsolidatedInventoryConnections();
    if (!allConnections?.length) {
      return;
    }

    const validConnections = filterValidConnections(allConnections);

    const sources = validConnections.filter((connection) =>
      connection.supportedTerminusTypes.includes(InventoryConnectionTerminusTypeEnum.Source),
    );
    // TODO: Remove magic string - https://auctane.atlassian.net/browse/SPD-21341
    const shouldAddShipstationInventoryConnection = sources.length === 1 && sources[0].apiCode === 'shipengine';
    if (shouldAddShipstationInventoryConnection) {
      validConnections.push({
        connectionId: sources[0].connectionId,
        terminusType: InventoryConnectionTerminusTypeEnum.Source,
      });
    }

    setSelectedConnections(
      validConnections.map((connection) => ({
        connectionId: connection.connectionId,
        terminusType: connection.terminusType,
      })),
    );
  }, [fetchConsolidatedInventoryConnections, isUpdatingConfig]);

  useOnComponentMount(() => {
    fetchAndSetActiveConfig();
  });

  const dispatch = useDispatch();

  const inventoryTrackingEnabled = useSellerSetting(SellerSettings.InventoryTrackingEnabled);

  useOnComponentMount(() => {
    const turnOnInventoryTracking = async () => {
      try {
        const settings = {
          [SellerSettings.InventoryTrackingEnabled.key]: true,
        };
        await updateSellerSettingsBulk(settings);
        dispatch(saveNewSellerSettings(settings));
      } catch {
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage {...messages.inventoryTrackingEnableErrorTitle} />,
          content: <FormattedMessage {...messages.inventoryTrackingEnableErrorContent} />,
        });
      }
    };
    if (!inventoryTrackingEnabled) {
      turnOnInventoryTracking();
    }
  });

  const marketplaces = useSelector(Global.selectReferenceDatagetMarketplaces);
  const fulfillmentProviders = useSelector(Global.selectReferenceDatagetFulfillmentProviders);

  const { sources, targets } = useMemo(() => {
    const validConnections = filterValidConnections(originalConnections);

    const prepareConnections = (allConnections, terminusType) => {
      return allConnections
        .filter((connection) => connection.supportedTerminusTypes.includes(terminusType))
        .map((connection) => ({
          ...connection,
          marketplaceId: marketplaces.find((mp) => mp.apiCode === connection.apiCode)?.marketplaceId,
          fillProviderId: fulfillmentProviders.find((fp) => fp.apiCode === connection.apiCode)?.fillProviderId,
          isShipstationInventory: connection.apiCode === 'shipengine' && terminusType === InventoryConnectionTerminusTypeEnum.Source,
        }));
    };

    return {
      targets: prepareConnections(validConnections, InventoryConnectionTerminusTypeEnum.Target),
      sources: prepareConnections(validConnections, InventoryConnectionTerminusTypeEnum.Source),
    };
  }, [originalConnections, fulfillmentProviders, marketplaces]);

  const toggleConnection = (connectionId, terminusType) => {
    const existingConnection = selectedConnections.find((connection) => connection.connectionId === connectionId);

    if (existingConnection) {
      // If the connection exists with the SAME terminus type, toggle it to None
      if (existingConnection.terminusType === terminusType) {
        setSelectedConnections((prev) =>
          prev.map((connection) => {
            if (connection.connectionId === connectionId) {
              return {
                connectionId,
                terminusType: InventoryConnectionTerminusTypeEnum.None,
              };
            }
            return connection;
          }),
        );
      } else {
        // If the connection exists with a DIFFERENT terminus type, update it to the new type
        setSelectedConnections((prev) =>
          prev.map((connection) => {
            if (connection.connectionId === connectionId) {
              return {
                connectionId,
                terminusType,
              };
            }
            return connection;
          }),
        );
      }
      return;
    }

    // Connection doesn't exist in the list, add it
    setSelectedConnections((prev) => [...prev, { connectionId, terminusType }]);
  };

  const { persistedInventoryManagementOption, updateInventoryManagementOption: persistInventoryManagementOption } = useFTUWizardContext();

  const [inventoryManagementOption, setInventoryManagementOption] = useState(() => persistedInventoryManagementOption || null);
  const [chosenInventoryManagementOption, setChosenInventoryManagementOption] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);

  if (!hasInitialized && persistedInventoryManagementOption) {
    setInventoryManagementOption(persistedInventoryManagementOption);
    setHasInitialized(true);
  }

  const updateInventoryManagementOption = useCallback(
    (option) => {
      Segment.trackEvent(ClickEvents.FTU_INVENTORY_MANAGEMENT_OPTION_SELECTED, {
        source: EventSourceStrings.FTU_WIZARD_INVENTORY_STEP,
        inventory_management_option: option,
      });
      setInventoryManagementOption(option);
      setChosenInventoryManagementOption(null);
      dispatch(clearImportStatus(ImportType.productWithInventory));
      Promise.resolve().then(() => {
        persistInventoryManagementOption(option);
      });
    },
    [dispatch, persistInventoryManagementOption],
  );

  const [inventorySubstep, setInventorySubstep] = useState();

  const updateInventorySubstep = (substep) => {
    setInventorySubstep(substep);
  };

  const confirmInventoryManagementOption = () => {
    setChosenInventoryManagementOption(inventoryManagementOption);
  };

  const value = {
    loading,
    sources,
    targets,
    connections: originalConnections,
    selectedConnections,
    updateConfig,
    toggleConnection,
    fetchConsolidatedInventoryConnections: fetchAndSetActiveConfig,
    inventoryManagementOption,
    updateInventoryManagementOption,
    inventorySubstep,
    updateInventorySubstep,
    chosenInventoryManagementOption,
    confirmInventoryManagementOption,
  };

  return <InventorySyncContext.Provider value={value}>{children}</InventorySyncContext.Provider>;
};
